import { useRouter } from 'next/router';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useSWR from 'swr';
import LightGallery from 'lightgallery/react';
import lgThumbnail from 'lightgallery/plugins/thumbnail';
import lgZoom from 'lightgallery/plugins/zoom';
import lgShare from 'lightgallery/plugins/share';
import lgFullscreen from 'lightgallery/plugins/fullscreen';

import PoiMedia, { ImageFile } from 'models/PoiMedia';

import { LightGallery as LightGalleryType } from 'lightgallery/lightgallery';

import MobileSwipeGallery from './gallery/MobileSwipeGallery';
import MultipleImages from './gallery/MultipleImages';
import SingleImage from './gallery/SingleImage';
import { Mixpanel } from 'utils/Mixpanel';

import 'lightgallery/css/lightgallery.css';
import 'lightgallery/css/lg-zoom.css';
import 'lightgallery/css/lg-thumbnail.css';
import 'lightgallery/css/lg-share.css';
import 'lightgallery/css/lg-fullscreen.css';

export interface DisplayImage {
    index: number;
    id: number | string;
    key: number | string;
    original: ImageFile;
    thumbnail: ImageFile;
    medium: ImageFile;
    large: ImageFile;
    alt: string;
    caption: string;
    created_at: string;
}

const getImageAuthor = (media: PoiMedia) => {
    if (media.user) return media?.user?.first_name + ' ' + media?.user?.last_name;
    if (media.author) {
        return media.author;
    }
    return media.meta?.author;
};

const mapPoiMedia = (poiMedia: PoiMedia[], poiName: string): DisplayImage[] => {
    return poiMedia.map((media, i) => {
        return {
            index: i + 1,
            id: media.id,
            key: media.id,
            original: media.files.original,
            thumbnail:
                media.files.conversions?.xs ??
                media.files.conversions?.thumb ??
                media.files.conversions?.large ??
                media.files.original,
            medium: media.files.conversions?.medium ?? media.files.original,
            large: media.files.conversions?.large ?? media.files.original,
            alt: poiName,
            caption: getImageAuthor(media) ? `Avtor fotografije: ${getImageAuthor(media)}` : '',
            created_at: media?.created_at
        };
    });
};

function PoiMediaGallery({ media, poiName, poiId }: { media: PoiMedia[]; poiName: string; poiId: number | string }) {
    const router = useRouter();
    const lightGallery = useRef<LightGalleryType | null>(null);
    const [galleryImages, setGalleryImages] = useState<DisplayImage[]>([]);

    const { data } = useSWR(`/public/poi/${poiId}/media`);
    const fetchedMedia: PoiMedia[] = data?.data || [];
    const images = useMemo(() => mapPoiMedia(media, poiName), [media, poiName]);

    useEffect(() => {
        if (fetchedMedia.length > 0) {
            setGalleryImages(mapPoiMedia(fetchedMedia, poiName));
        }
    }, [fetchedMedia]);

    const onPhotoClick = (index) => {
        Mixpanel.track('Gallery opened');
        if (isNaN(index)) index = 1;

        if (lightGallery?.current) {
            lightGallery?.current.openGallery(index - 1);
        }
    };

    useEffect(() => {
        const onRouteChange = () => {
            lightGallery?.current.closeGallery();
        };

        router.events.on('routeChangeComplete', onRouteChange);
        return () => {
            router.events.off('routeChangeComplete', onRouteChange);
        };
    }, []);

    const onInit = useCallback((detail) => {
        if (detail) {
            lightGallery.current = detail.instance;
        }
    }, []);

    return (
        <>
            <div className="hidden sm:block relative h-[420px] lg:h-[480px] xl:h-[520px]">
                {images.length <= 2 && <SingleImage image={images[0]} handlePhotoClick={() => onPhotoClick(1)} />}
                {images.length > 2 && <MultipleImages images={images} handlePhotoClick={onPhotoClick} />}
            </div>
            <div className="sm:hidden relative h-[320px]">
                <MobileSwipeGallery images={images} handleClick={onPhotoClick} />
            </div>
            <LightGallery
                licenseKey="2852F19C-5E994C1D-B55C14A3-F04F3C90"
                plugins={[lgZoom, lgThumbnail, lgShare, lgFullscreen]}
                onInit={onInit}
                // dynamic
                // dynamicEl={getGalleryItems()}
                mobileSettings={{ showCloseIcon: true, fullScreen: false, download: false }}
            >
                {galleryImages.map((item) => (
                    <a key={item.key} href={item.large.url} className="hidden">
                        <img
                            loading="lazy"
                            alt={item.caption}
                            src={item.thumbnail.url}
                            width={100}
                            height={100}
                            className="lazy"
                            data-original={item.large.url}
                            data-responsive={`${item.thumbnail.url} ${item.thumbnail.width}, ${item.medium.url} ${item.medium.width}, ${item.large.url} ${item.large.width}`}
                        />
                    </a>
                ))}
            </LightGallery>
        </>
    );
}

export default PoiMediaGallery;
